import { observer } from 'mobx-react';
import { Box, Typography } from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';

// Real license number/state/expiry/document come from the overview's
// files.gc_license / files.broker_license. Also used by OverviewTab.
export const buildLicenseRows = (verificationStore: any) => {
  const borrowerInfo = verificationStore?.borrowerInfo || {};
  const files = verificationStore?.trackRecordOverview?.files || {};
  const rows = [
    {
      key: 'gc',
      defaultName: 'General Contractor',
      claimed: !!borrowerInfo.isGeneralContractor,
      uploads: verificationStore?.generalContractorUploads || [],
      file: files.gc_license,
    },
    {
      key: 're',
      defaultName: 'Real Estate Agent',
      claimed: !!borrowerInfo.isBroker,
      uploads: verificationStore?.brokerUploads || [],
      file: files.broker_license,
    },
  ];
  return rows.map((row) => {
    const license = row.file?.license;
    const document = row.file?.documents?.[0];
    return {
      key: row.key,
      name: license?.license_type || row.defaultName,
      number: license?.license_number ?? '—',
      state: license?.license_state ?? '—',
      expires: license?.license_expiry_date ?? '—',
      status: (document || row.uploads.length > 0) ? 'verified' : 'needsInput',
      document,
      taskId: row.file?.task_id,
      claimed: row.claimed || !!document || row.uploads.length > 0,
    };
  });
};

// Used by the sidebar nav badges — same verified/needsInput split as this tab.
export const getLicensesStatusCounts = (verificationStore: any) => {
  const rows = buildLicenseRows(verificationStore);
  const verified = rows.filter((row) => row.status === 'verified').length;
  return { verified, needsInput: rows.length - verified };
};

// Shared download handler (LicensesTab and the Overview tab's license row).
export const downloadLicenseDocument = (
  documentStore: any,
  verificationStore: any,
  document: any,
  taskId: any,
) => {
  if (!document) return;
  const fileExtension = (document.original_file_name || '').split('.').pop();
  if (documentStore?.downloadDocument) {
    documentStore.downloadDocument({
      taskId,
      originalFileName: document.original_file_name,
      fileExtension,
      taskName: document.original_file_name,
    });
  } else {
    verificationStore?.downloadTaskFile?.(taskId, document.original_file_name, fileExtension);
  }
};

export const LicensesTab = observer(({
  classes,
  verificationStore,
  documentStore,
}: {
  classes: Record<string, string>;
  verificationStore?: any;
  documentStore?: any;
}) => {
  // Already loaded by TrackRecordV2's single loadFromOverview() call on mount.
  const licenses = buildLicenseRows(verificationStore);

  const handleDownload = (document: any, taskId: any) =>
    downloadLicenseDocument(documentStore, verificationStore, document, taskId);

  return (
    <>
      <Typography variant="h5" className={classes.overviewTitle}>
        Licenses
      </Typography>
      <Typography className={classes.overviewSubtitle}>
        Contractor and broker licenses claimed on the eligibility questions.
      </Typography>

      <Box className={classes.table}>
        <Box className={`${classes.licenseGridRow} ${classes.tableHeaderRow}`}>
          <Typography className={classes.tableHeaderCell}>License</Typography>
          <Typography className={classes.tableHeaderCell}>Number</Typography>
          <Typography className={classes.tableHeaderCell}>State</Typography>
          <Typography className={classes.tableHeaderCell}>Expires</Typography>
          <Typography className={classes.tableHeaderCell}>Document</Typography>
          <Typography className={classes.tableHeaderCell}>Status</Typography>
        </Box>
        {licenses.map((license) => (
          <Box key={license.key} className={classes.licenseGridRow}>
            <Typography className={classes.tableCellPrimary}>{license.name}</Typography>
            <Typography className={classes.tableCell}>{license.number}</Typography>
            <Typography className={classes.tableCell}>{license.state}</Typography>
            <Typography className={classes.tableCell}>{license.expires}</Typography>
            {license.document ? (
              <Box
                className={classes.uploadLink}
                style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}
                onClick={() => handleDownload(license.document, license.taskId)}
              >
                <GetAppIcon style={{ fontSize: 16 }} />
                <Typography className={classes.tableCell} style={{ color: 'inherit' }} noWrap>
                  {license.document.original_file_name}
                </Typography>
              </Box>
            ) : (
              <Typography className={classes.tableCell}>—</Typography>
            )}
            <span
              className={`${classes.statusPill} ${
                license.status === 'verified' ? classes.statusVerified : classes.statusNeedsInput
              }`}
            >
              <span
                className={`${classes.statusDot} ${
                  license.status === 'verified' ? classes.statusVerifiedDot : classes.statusNeedsInputDot
                }`}
              />
              {license.status === 'verified' ? 'Verified' : 'Not Verified'}
            </span>
          </Box>
        ))}
      </Box>
    </>
  );
});
